import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Check, X, Loader2, Edit3, User, Briefcase, Tag, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Profile Builder Modal
 * Generates professional profile content from natural language description.
 * 
 * Features:
 * - Single input box for describing background/experience
 * - Preview cards showing generated: Bio, Headline, Skills/Tags, Category, Description
 * - Actions: Accept & Apply, Edit Manually, Regenerate, Cancel
 * - Never overwrites without user confirmation
 */
export default function AIProfileBuilder({ 
  isOpen, 
  onClose, 
  profileType = "talent",
  onApply,
  existingData = {} 
}) {
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(null);

  const handleGenerate = async () => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error("Please describe yourself in at least 10 characters");
      return;
    }

    setLoading(true);
    setGenerated(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/generate-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          description: description.trim(),
          profile_type: profileType
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to generate profile content");
      }

      setGenerated(data.generated);
      setEditedContent(data.generated);
      toast.success("Profile content generated!");
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error(error.message || "Failed to generate content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    setGenerated(null);
    setEditedContent(null);
    setEditMode(false);
    handleGenerate();
  };

  const handleApply = () => {
    const contentToApply = editMode ? editedContent : generated;
    
    // Check if there's existing data that would be overwritten
    const hasExistingData = existingData.bio || existingData.headline || 
      (existingData.skills && existingData.skills.length > 0);
    
    if (hasExistingData) {
      const confirmed = window.confirm(
        "This will update your existing profile content. Are you sure you want to apply these AI-generated suggestions?"
      );
      if (!confirmed) return;
    }
    
    onApply(contentToApply);
    handleClose();
    toast.success("AI suggestions applied to your profile!");
  };

  const handleClose = () => {
    setDescription("");
    setGenerated(null);
    setEditedContent(null);
    setEditMode(false);
    onClose();
  };

  const handleEditField = (field, value) => {
    setEditedContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getProfileTypeLabel = () => {
    switch (profileType) {
      case "trainer": return "Trainer";
      case "enterprise": return "Company";
      default: return "Talent";
    }
  };

  const getPlaceholderText = () => {
    switch (profileType) {
      case "trainer":
        return "Example: I'm a voice coach with 10 years of experience training actors for film and theatre. I specialize in accent work and projection techniques. I've worked with major production houses and trained over 500 students...";
      case "enterprise":
        return "Example: We're a boutique production house based in Mumbai, specializing in ad films and digital content. We've worked with major brands like Nike and Coca-Cola. Our team has won 3 Cannes Lions...";
      default:
        return "Example: I'm a theatre actor with 5 years of stage experience and some short film work. I trained at NSD and specialize in method acting. I've done mainly Hindi drama but also comfortable with comedy...";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" />
            Build with AI
          </DialogTitle>
          <DialogDescription>
            Describe your background and let AI create a professional {getProfileTypeLabel()} profile for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Input Section */}
          {!generated && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Describe yourself / your experience
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={getPlaceholderText()}
                className="min-h-[150px] resize-none"
                data-testid="ai-profile-description-input"
              />
              <p className="text-xs text-gray-500">
                Be specific about your experience, skills, and what makes you unique.
              </p>
              
              <Button 
                onClick={handleGenerate}
                disabled={loading || description.trim().length < 10}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                data-testid="ai-generate-profile-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Profile Content
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Results Section */}
          {generated && (
            <div className="space-y-4">
              {/* Preview Cards */}
              <div className="grid gap-3">
                {/* Headline */}
                <Card className="border-violet-200 bg-violet-50/50">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
                      <User className="w-4 h-4" />
                      Headline
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-4">
                    {editMode ? (
                      <input
                        type="text"
                        value={editedContent.headline}
                        onChange={(e) => handleEditField("headline", e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    ) : (
                      <p className="font-medium text-gray-800">{generated.headline}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Bio */}
                <Card className="border-violet-200 bg-violet-50/50">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
                      <FileText className="w-4 h-4" />
                      Bio
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-4">
                    {editMode ? (
                      <Textarea
                        value={editedContent.bio}
                        onChange={(e) => handleEditField("bio", e.target.value)}
                        className="min-h-[100px] text-sm"
                      />
                    ) : (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{generated.bio}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Category & Skills */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="border-violet-200 bg-violet-50/50">
                    <CardHeader className="py-2 px-4">
                      <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
                        <Briefcase className="w-4 h-4" />
                        Category
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-4">
                      {editMode ? (
                        <input
                          type="text"
                          value={editedContent.category}
                          onChange={(e) => handleEditField("category", e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        <Badge className="bg-violet-600">{generated.category}</Badge>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-violet-200 bg-violet-50/50">
                    <CardHeader className="py-2 px-4">
                      <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
                        <Tag className="w-4 h-4" />
                        Skills / Tags
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-4">
                      {editMode ? (
                        <input
                          type="text"
                          value={editedContent.skills?.join(", ") || ""}
                          onChange={(e) => handleEditField("skills", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                          placeholder="Comma-separated skills"
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {generated.skills?.map((skill, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-violet-300 text-violet-700">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Description */}
                <Card className="border-violet-200 bg-violet-50/50">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
                      <FileText className="w-4 h-4" />
                      Short Description
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-4">
                    {editMode ? (
                      <Textarea
                        value={editedContent.description}
                        onChange={(e) => handleEditField("description", e.target.value)}
                        className="min-h-[60px] text-sm"
                      />
                    ) : (
                      <p className="text-sm text-gray-700">{generated.description}</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button 
                  onClick={handleApply}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  data-testid="ai-apply-profile-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Accept & Apply
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => setEditMode(!editMode)}
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="ai-edit-profile-btn"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {editMode ? "Preview" : "Edit"}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={loading}
                  className="border-gray-300"
                  data-testid="ai-regenerate-profile-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Regenerate
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={handleClose}
                  className="text-gray-500"
                  data-testid="ai-cancel-profile-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
